import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyWarning } from '@repletoryreach/sdk-ui/notifications';
import { isApiError, settings } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { formatInteger } from '../../../lib/format';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import type { Queue } from './queueState';

const MUTED = 'var(--rr-color-text-muted)';

/** The service's own ceiling. There is deliberately no "move everything". */
const MAX_COUNT = 500;

/**
 * Every code the endpoint answers with, and the sentence that tells the
 * operator what it means for their messages.
 *
 * Three of these are not the operator's mistake and each needs its own
 * sentence: an empty queue means the depth on screen was stale, an unreachable
 * broker means nothing moved, and an unwritable audit row means the service
 * refused to act unrecorded. "Something went wrong" would leave an operator
 * guessing whether customer messages are still on the queue.
 */
const ERROR_KEYS: Record<string, string> = {
  NO_SUCH_QUEUE: 'queues.requeue.error.noSuchQueue',
  NO_SUCH_TARGET: 'queues.requeue.error.noSuchTarget',
  TARGET_IS_SOURCE: 'queues.requeue.error.targetIsSource',
  INVALID_COUNT: 'queues.requeue.error.invalidCount',
  REASON_REQUIRED: 'queues.requeue.error.reasonRequired',
  EMPTY_QUEUE: 'queues.requeue.error.emptyQueue',
  BROKER_UNREACHABLE: 'queues.requeue.error.brokerUnreachable',
  AUDIT_UNAVAILABLE: 'queues.requeue.error.auditUnavailable'
};

/** How the service worked out where a message belonged, in words. */
const SOURCE_KEYS: Record<string, string> = {
  target: 'queues.requeue.result.source.target',
  x_death: 'queues.requeue.result.source.xDeath',
  fault_input_address: 'queues.requeue.result.source.faultAddress',
  queue_name: 'queues.requeue.result.source.queueName'
};

function errorMessage(t: Translate, error: unknown, queue: string, target: string): string {
  if (!isApiError(error)) {
    return t('queues.requeue.error.unexpected', {
      message: error instanceof Error ? error.message : t('common.unknownError')
    });
  }
  if (error.status === 403) return t('queues.requeue.error.forbidden');
  const key = ERROR_KEYS[error.code];
  if (key) return t(key, { queue, target, max: MAX_COUNT });
  return t('queues.requeue.error.generic', { code: error.code, message: error.message });
}

/** One destination, with how many messages reached it and on what evidence. */
interface Destination {
  routingKey: string;
  source: string;
  count: number;
}

function destinations(moves: settings.RequeueMove[]): Destination[] {
  const groups = new Map<string, Destination>();
  for (const move of moves) {
    if (move.outcome !== 'requeued') continue;
    const key = `${move.routing_key} ${move.source}`;
    const existing = groups.get(key);
    if (existing) existing.count += 1;
    else groups.set(key, { routingKey: move.routing_key, source: move.source, count: 1 });
  }
  return Array.from(groups.values()).sort((a, b) => b.count - a.count);
}

function Figure({ label, value, tone, hint }: { label: string; value: string; tone?: string; hint?: string }) {
  return (
    <Stack gap="xs" style={{ minWidth: 96 }}>
      <Text role="caption" c={MUTED}>{label}</Text>
      <Text role="subtitle" fw={700} c={tone}>{value}</Text>
      {hint && <Text role="caption" c={MUTED}>{hint}</Text>}
    </Stack>
  );
}

/**
 * Moving dead-lettered messages back onto the queue that should have handled
 * them, and saying exactly what that will do before it happens.
 *
 * The dialog is the confirmation: the statement above the controls is rewritten
 * as the operator types, so the count, the source queue and the destination on
 * screen are the ones the confirm button will act on. A second "are you sure"
 * over the top would only put a vague sentence in front of a precise one.
 *
 * After the move the same dialog becomes the receipt rather than closing.
 * `restored` and `failed` are the two figures worth reading, and a toast that
 * vanishes in four seconds is the wrong place for them.
 */
export function RequeueDialog({ queue, held, onClose, onMoved }: {
  /**
   * The row this acts on, captured when the dialog opened. A snapshot rather
   * than a lookup into the polling list, so a broker that answers one poll with
   * an empty list cannot close the dialog and take the receipt with it.
   */
  queue: Queue | null;
  /** The freshest depth the page knows for that queue, which the snapshot's own figure would go stale against. */
  held: number;
  onClose: () => void;
  /** Fired after a successful move, so the page can refetch the depths it is showing. */
  onMoved: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();

  const [count, setCount] = useState<number | ''>(1);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<settings.RequeueResult | null>(null);

  const name = queue?.name ?? '';
  const wanted = typeof count === 'number' ? count : 0;
  const trimmedTarget = target.trim();
  const trimmedReason = reason.trim();

  const move = useMutation({
    mutationFn: (input: { queue: string; count: number; target: string | null; reason: string }) =>
      settings.requeueQueue(input.queue, { count: input.count, target: input.target, reason: input.reason }),
    onSuccess: (envelope) => {
      const data = envelope.data;
      setResult(data);
      // The audit trail on this page has just gained a row, and the depths in
      // the table are now wrong until the next poll.
      qc.invalidateQueries({ queryKey: ['admin', 'ops', 'audit'] });
      onMoved();
      if (data.restored > 0 || data.failed > 0) {
        notifyWarning(
          t('queues.requeue.toast.partialBody', {
            requeued: formatInteger(data.requeued),
            restored: formatInteger(data.restored),
            failed: formatInteger(data.failed),
            queue: data.queue
          }),
          t('queues.requeue.toast.partialTitle')
        );
      } else if (data.stopped) {
        // Not a failure, so not an error toast: what moved, moved. It is amber
        // because believing a queue is drained when it is not is the exact
        // mistake this endpoint exists to prevent.
        notifyWarning(
          t('queues.requeue.toast.stoppedBody', { requeued: formatInteger(data.requeued), queue: data.queue }),
          t('queues.requeue.toast.stoppedTitle')
        );
      } else {
        notifySuccess(
          data.requeued === 1
            ? t('queues.requeue.toast.bodyOne', { queue: data.queue })
            : t('queues.requeue.toast.body', { requeued: formatInteger(data.requeued), queue: data.queue }),
          t('queues.requeue.toast.title')
        );
      }
    }
  });

  // Every field is local, so without this the next queue inherits the previous
  // count, destination and reason. A reason copied onto the wrong queue's audit
  // row is a lie in the record.
  useEffect(() => {
    if (queue === null) return;
    setCount(1);
    setTarget('');
    setReason('');
    setResult(null);
    move.reset();
    // Resetting on the queue, not on `opened`, also covers reopening the dialog
    // on a different queue without it closing in between.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue?.vhost, queue?.name]);

  /**
   * What the run never got to, worked out from the counts rather than from the
   * server's sentence: parsing prose for a number is the kind of cleverness
   * that breaks silently when the wording changes.
   */
  function notAttempted(r: settings.RequeueResult): number {
    return Math.max(0, wanted - (r.requeued + r.restored + r.failed));
  }

  /** Carry the same reason and the outstanding count back into the form. */
  function continueRun(r: settings.RequeueResult) {
    setCount(Math.min(MAX_COUNT, Math.max(1, notAttempted(r))));
    setResult(null);
    move.reset();
  }

  function submit() {
    if (!queue || !trimmedReason) return;
    move.mutate({
      queue: queue.name,
      count: wanted,
      target: trimmedTarget || null,
      reason: trimmedReason
    });
  }

  const failure = move.error ? errorMessage(t, move.error, name, trimmedTarget) : null;
  const overflow = wanted > held;
  const routedBack = trimmedTarget.length === 0;

  return (
    <Modal
      opened={queue !== null}
      onClose={onClose}
      size="lg"
      title={result
        ? t('queues.requeue.result.title', { queue: name })
        : t('queues.requeue.title', { queue: name })}
    >
      {queue && result === null && (
        <Stack gap="lg">
          {/* What the confirm button will do, in the operator's own numbers.
              This is the part that must be exact: the action moves customer
              money and customer messages. */}
          <Alert color="warning" title={t('queues.requeue.summaryTitle')}>
            <Stack gap="xs">
              <Text role="bodySmall">
                {overflow
                  ? t('queues.requeue.summaryCountOverflow', {
                      count: formatInteger(wanted),
                      held: formatInteger(held),
                      queue: name
                    })
                  : t('queues.requeue.summaryCount', {
                      count: formatInteger(wanted),
                      held: formatInteger(held),
                      queue: name
                    })}
              </Text>
              <Text role="bodySmall">
                {routedBack
                  ? t('queues.requeue.summaryFaultAddress')
                  : t('queues.requeue.summaryTarget', { target: trimmedTarget })}
              </Text>
              <Text role="bodySmall">{t('queues.requeue.summaryRetry', { queue: name })}</Text>
              <Text role="bodySmall" c={MUTED}>
                {t('queues.requeue.summaryAudit', {
                  actor: user?.display_name || user?.email || t('queues.audit.unknownActor')
                })}
              </Text>
            </Stack>
          </Alert>

          <NumberInput
            label={t('queues.requeue.count')}
            description={t('queues.requeue.countHint', { max: MAX_COUNT, held: formatInteger(held) })}
            min={1}
            max={MAX_COUNT}
            value={count}
            onChange={setCount}
            disabled={move.isPending}
          />

          <TextInput
            label={t('queues.requeue.target')}
            description={t('queues.requeue.targetHint')}
            placeholder={t('queues.requeue.targetPlaceholder')}
            value={target}
            onChange={(e) => setTarget(e.currentTarget.value)}
            disabled={move.isPending}
          />

          <Textarea
            label={t('queues.requeue.reason')}
            description={t('queues.requeue.reasonHint')}
            placeholder={t('queues.requeue.reasonPlaceholder')}
            required
            withAsterisk
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            disabled={move.isPending}
          />

          {failure && (
            <Alert color="error" title={t('queues.requeue.error.title')}>{failure}</Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={move.isPending}>{t('action.cancel')}</Button>
            <Button
              color="orange"
              onClick={submit}
              loading={move.isPending}
              disabled={trimmedReason.length === 0 || wanted < 1}
            >
              {wanted === 1
                ? t('queues.requeue.confirmOne')
                : t('queues.requeue.confirm', { count: formatInteger(wanted) })}
            </Button>
          </Group>
        </Stack>
      )}

      {queue && result && (
        <Stack gap="lg">
          <Group gap="xl" wrap="wrap">
            <Figure
              label={t('queues.requeue.result.requeued')}
              value={formatInteger(result.requeued)}
              tone={result.requeued > 0 ? 'var(--rr-color-success)' : undefined}
            />
            <Figure
              label={t('queues.requeue.result.restored')}
              value={formatInteger(result.restored)}
              tone={result.restored > 0 ? 'var(--rr-color-warning)' : undefined}
            />
            <Figure
              label={t('queues.requeue.result.failed')}
              value={formatInteger(result.failed)}
              tone={result.failed > 0 ? 'var(--rr-color-error)' : undefined}
            />
            {/* The broker serves this from a statistics database it samples on
                its own interval, so it is routinely absent or a few seconds
                behind. A confident zero here would be a lie. */}
            <Figure
              label={t('queues.requeue.result.remaining')}
              value={result.remaining == null ? t('common.notAvailable') : formatInteger(result.remaining)}
              hint={t('queues.requeue.result.remainingHint')}
            />
          </Group>

          {/* A run given a wall-clock budget stops early and says so rather than
              being cut off by a proxy after the messages have already moved.
              Info, never error: the counts beside it are true. Untested against
              a live stop, which needs a broker slow enough to exhaust the
              service's budget. */}
          {result.stopped && (
            <Alert color="info" title={t('queues.requeue.result.stoppedTitle')}>
              <Stack gap="sm">
                <Text role="bodySmall">{result.stopped}</Text>
                <Text role="caption" c={MUTED}>{t('queues.requeue.result.stoppedCounts')}</Text>
                <Group>
                  <Button size="sm" variant="light" onClick={() => continueRun(result)}>
                    {t('queues.requeue.result.stoppedAgain')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          {result.restored > 0 && (
            <Alert
              color="warning"
              title={result.restored === 1
                ? t('queues.requeue.result.restoredTitleOne')
                : t('queues.requeue.result.restoredTitle', { count: formatInteger(result.restored) })}
            >
              {result.restored === 1
                ? t('queues.requeue.result.restoredBodyOne', { queue: result.queue })
                : t('queues.requeue.result.restoredBody', { queue: result.queue })}
            </Alert>
          )}

          {result.failed > 0 && (
            <Alert
              color="error"
              title={result.failed === 1
                ? t('queues.requeue.result.failedTitleOne')
                : t('queues.requeue.result.failedTitle', { count: formatInteger(result.failed) })}
            >
              {t('queues.requeue.result.failedBody')}
            </Alert>
          )}

          {destinations(result.moves).length > 0 && (
            <Stack gap="sm">
              <Text role="bodySmall" fw={600}>{t('queues.requeue.result.destinations')}</Text>
              {destinations(result.moves).map((d) => (
                <Group key={`${d.routingKey}/${d.source}`} justify="space-between" align="baseline" gap="md" wrap="nowrap">
                  <Text role="bodySmall"><code>{d.routingKey}</code></Text>
                  <Text role="caption" c={MUTED}>
                    {t('queues.requeue.result.destinationRow', {
                      count: formatInteger(d.count),
                      source: t(SOURCE_KEYS[d.source] ?? 'queues.requeue.result.source.queueName')
                    })}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}

          <Text role="caption" c={MUTED}>{t('queues.requeue.result.recorded')}</Text>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => { setResult(null); setReason(''); move.reset(); }}
            >
              {t('queues.requeue.result.again')}
            </Button>
            <Button onClick={onClose}>{t('action.close')}</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
